import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import './Gallery.css';
import Title from "../../shared/ui/Title/title";

const Gallery = () => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [categories, setCategories] = useState(['Все']);
  const [activeCategory, setActiveCategory] = useState('Все');
  const [filteredPhotos, setFilteredPhotos] = useState([]);
  
  // Состояния для пагинации
  const [visibleCount, setVisibleCount] = useState(10);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Отслеживание размера экрана
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      // Сбрасываем количество видимых фото при изменении размера экрана
      setVisibleCount(10);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Загрузка фото из Firestore
  useEffect(() => {
    loadPhotos();
  }, []);

  const loadPhotos = async () => {
    try {
      setLoading(true);
      const q = query(collection(db, 'gallery'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const loadedPhotos = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        loadedPhotos.push({
          id: doc.id,
          ...data,
          imageSrc: data.imageType === 'base64' ? data.imageBase64 : data.imageUrl
        });
      });
      
      console.log('Загружено фото из Firestore:', loadedPhotos.length);
      setPhotos(loadedPhotos);
      setFilteredPhotos(loadedPhotos);
      
      const uniqueCategories = ['Все', ...new Set(loadedPhotos.map(p => p.category))];
      setCategories(uniqueCategories);
    } catch (error) {
      console.error('Ошибка загрузки из Firestore:', error);
    } finally {
      setLoading(false);
    }
  };

  // Фильтрация по категориям
  useEffect(() => {
    if (activeCategory === 'Все') {
      setFilteredPhotos(photos);
    } else {
      setFilteredPhotos(photos.filter(photo => photo.category === activeCategory));
    }
    // Сбрасываем количество видимых фото при смене категории
    setVisibleCount(10);
  }, [activeCategory, photos]);

  // Функция для загрузки еще фото
  const loadMorePhotos = () => {
    setVisibleCount(prevCount => prevCount + 10);
  };

  // Получаем видимые фото в зависимости от устройства
  const getVisiblePhotos = () => {
    if (isMobile) {
      return filteredPhotos.slice(0, visibleCount);
    }
    return filteredPhotos; // На десктопе показываем все
  };

  const visiblePhotos = getVisiblePhotos();
  const hasMorePhotos = isMobile && visiblePhotos.length < filteredPhotos.length;

  const openPhoto = (photo) => {
    setSelectedPhoto(photo);
    document.body.style.overflow = 'hidden';
  };

  const closePhoto = () => {
    setSelectedPhoto(null);
    document.body.style.overflow = 'auto';
  };

  const navigatePhoto = (direction) => {
    if (!selectedPhoto) return;
    
    const currentIndex = filteredPhotos.findIndex(p => p.id === selectedPhoto.id);
    let newIndex;
    
    if (direction === 'next') {
      newIndex = currentIndex + 1 < filteredPhotos.length ? currentIndex + 1 : 0;
    } else {
      newIndex = currentIndex - 1 >= 0 ? currentIndex - 1 : filteredPhotos.length - 1;
    }
    
    setSelectedPhoto(filteredPhotos[newIndex]);
  };

  // Обработка клавиш
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!selectedPhoto) return;
      
      if (e.key === 'Escape') {
        closePhoto();
      } else if (e.key === 'ArrowRight') {
        navigatePhoto('next');
      } else if (e.key === 'ArrowLeft') {
        navigatePhoto('prev');
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedPhoto]);

  if (loading) {
    return (
      <section className="gallery" id="Work">
        <Title title="Мои работы" translate="Work" />
        <div className="gallery-loader">
          <div className="loader-spinner"></div>
          <p>Загружаем фотографии из Firestore...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="gallery" id="Work">
      <Title title="Мои работы" translate="Work" />
      
      {/* Категории */}
      {categories.length > 1 && (
        <div className="gallery-categories">
          {categories.map((category) => (
            <button
              key={category}
              className={`category-button ${activeCategory === category ? 'active' : ''}`}
              onClick={() => setActiveCategory(category)}
            >
              {category}
            </button>
          ))}
        </div>
      )}

      {/* Сетка фотографий */}
      <div className="gallery-grid">
        {visiblePhotos.length > 0 ? (
          visiblePhotos.map((photo, index) => (
            <div
              key={photo.id}
              className="gallery-item"
              onClick={() => openPhoto(photo)}
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              <div className="item-image">
                <img 
                  src={photo.imageSrc} 
                  alt={photo.title}
                  loading="lazy"
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/400x300?text=Фото+не+загрузилось';
                  }}
                />
                <div className="item-overlay">
                  <span className="overlay-icon">🔍</span>
                  <span className="overlay-text">{photo.category}</span>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="gallery-empty">
            <p>😕 Пока нет фотографий</p>
          </div>
        )}
      </div>

      {/* Кнопка "Показать еще" для мобильных устройств */}
      {hasMorePhotos && (
        <div className="gallery-load-more">
          <button 
            className="load-more-button"
            onClick={loadMorePhotos}
          >
            Показать еще ({filteredPhotos.length - visiblePhotos.length})
          </button>
        </div>
      )}

      {/* Информация о количестве фото для мобильных */}
      {isMobile && filteredPhotos.length > 0 && (
        <div className="gallery-stats">
          Показано {visiblePhotos.length} из {filteredPhotos.length} фото
        </div>
      )}

      {/* Модальное окно */}
      {selectedPhoto && (
        <div className="modal-overlay" onClick={closePhoto}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closePhoto}>×</button>
            
            <button className="modal-nav prev" onClick={() => navigatePhoto('prev')}>←</button>
            <button className="modal-nav next" onClick={() => navigatePhoto('next')}>→</button>
            
            <div className="modal-content">
              <img 
                src={selectedPhoto.imageSrc} 
                alt={selectedPhoto.title}
                className="modal-image"
                onError={(e) => {
                  e.target.src = 'https://via.placeholder.com/800x600?text=Фото+не+загрузилось';
                }}
              />
              <div className="modal-info">
                <h3 className="modal-title">{selectedPhoto.title}</h3>
                <p className="modal-category">{selectedPhoto.category}</p>
                {selectedPhoto.description && (
                  <p className="modal-description">{selectedPhoto.description}</p>
                )}
                <div className="modal-meta">
                  <span className="meta-date">
                    {new Date(selectedPhoto.createdAt).toLocaleDateString('ru-RU')}
                  </span>
                  <span className="meta-counter">
                    {filteredPhotos.findIndex(p => p.id === selectedPhoto.id) + 1} / {filteredPhotos.length}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Gallery;